import type React from 'react';

import { useTheme } from '@course-authoring/shared-types';

import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { SectionSurfaceProvider } from './SectionSurfaceContext';

/** Where a pinned overlay rests, measured from the section's top edge. */
const STICKY_OVERLAY_INSET = 'md';

/**
 * A pinned overlay travels over authored content, so it has to clear the
 * z-indexes block renderers use for their own layers (a dragged
 * Categorization card at 20, a Map region panel at 100). It stays well below
 * the modal/timer tier (9999+) so real dialogs still cover it.
 */
const STICKY_OVERLAY_Z_INDEX = 200;

export interface CourseSectionShellProps {
  children: React.ReactNode;
  index?: number;
  surface?: 'light' | 'dark';
  className?: string;
  style?: React.CSSProperties;
  innerStyle?: React.CSSProperties;
  /**
   * Rendered as the section's own first child in a zero-height sticky
   * wrapper, so it stays visible for as long as the section spans the
   * viewport. It must sit outside the inner content wrapper: that wrapper is
   * inset by the section's vertical padding, which would clip the sticky
   * range to exclude the card's visual edges. Used for the narration controls.
   *
   * Not a generic slot: the shell imposes the sticky, zero-height,
   * padding-compensated, z-indexed treatment on whatever is passed.
   */
  stickyOverlay?: React.ReactNode;
  /**
   * Distance in px from the scroll viewport's top edge that the pinned
   * `stickyOverlay` must clear — the height of any sticky top banner.
   */
  stickyOverlayOffset?: number;
  /**
   * NA-408 "Show Block IDs": rendered low inside the section's own bottom
   * padding, absolutely positioned against the section (not the inner content
   * wrapper) so it reaches the full padding depth regardless of the
   * mobile/small-mobile padding overrides computed below, width-capped and
   * centered to match the inner content wrapper's horizontal alignment. Adds
   * no layout height and never shifts the block's own position.
   */
  bottomOverlay?: React.ReactNode;
}

export function CourseSectionShell({
  children,
  index = 0,
  surface,
  className,
  style,
  innerStyle,
  stickyOverlay,
  stickyOverlayOffset = 0,
  bottomOverlay,
}: CourseSectionShellProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);

  const resolvedSurface = surface ?? (index % 2 === 0 ? 'dark' : 'light');
  const backgroundColor =
    resolvedSurface === 'dark'
      ? tokens.backgrounds.lessonSectionTinted
      : tokens.backgrounds.lessonSectionDefault;
  // NA-205: halve the between-block breathing room on narrow phones (≤576px).
  const sectionPaddingY = isSmallMobile
    ? '50px'
    : isMobile
      ? tokens.layout.sectionPaddingY.mobile
      : tokens.layout.sectionPaddingY.desktop;
  const sectionPaddingX = isMobile
    ? tokens.layout.sectionPaddingX.mobile
    : '0px';
  // NA-408: the block-id label's own fixed side margin — independent of the
  // block content's positioning above (which relies on max-width centering
  // alone on desktop, not a real section padding; see `sectionPaddingX`).
  // Reuses the existing `sectionPaddingX` tokens rather than the design
  // spec's illustrative "72px" literal, since 64px is this app's real
  // equivalent value and doesn't need a new one-off number introduced for
  // a single label. Deliberately does NOT track any specific block's own
  // internal layout (e.g. a block's extra image-top inset) — same margin
  // for every block, per spec.
  const idSideMargin = isMobile
    ? tokens.layout.sectionPaddingX.mobile
    : tokens.layout.sectionPaddingX.desktop;

  // The overlay is in flow, so it would otherwise start below the section's
  // vertical padding — on top of the first line of content. The negative top
  // margin pulls it back up to its resting inset inside the padding
  // whitespace, and the matching bottom margin leaves sibling flow untouched
  // and releases the pinned overlay before it can overhang the next section.
  const overlayInset = tokens.spacing[STICKY_OVERLAY_INSET];
  const stickyOverlayStyle: React.CSSProperties = {
    position: 'sticky',
    top: `calc(${stickyOverlayOffset}px + ${overlayInset})`,
    height: 0,
    marginTop: `calc(${overlayInset} - ${sectionPaddingY})`,
    marginBottom: `calc(${sectionPaddingY} - ${overlayInset})`,
    zIndex: STICKY_OVERLAY_Z_INDEX,
  };

  return (
    <section
      className={className}
      data-section-surface={resolvedSurface}
      style={{
        '--course-section-padding-y': sectionPaddingY,
        width: '100%',
        backgroundColor,
        padding: `${sectionPaddingY} ${sectionPaddingX}`,
        boxSizing: 'border-box',
        position: 'relative',
        ...style,
      } as React.CSSProperties}
    >
      {stickyOverlay ? (
        <div data-sticky-overlay="true" style={stickyOverlayStyle}>
          {stickyOverlay}
        </div>
      ) : null}
      <SectionSurfaceProvider value={resolvedSurface}>
        <div
          style={{
            width: '100%',
            maxWidth: tokens.layout.contentMaxWidth,
            margin: '0 auto',
            boxSizing: 'border-box',
            ...innerStyle,
          }}
        >
          {children}
        </div>
      </SectionSurfaceProvider>
      {bottomOverlay ? (
        <div
          data-block-id-overlay="true"
          style={{
            position: 'absolute',
            bottom: tokens.spacing.md,
            left: idSideMargin,
          }}
        >
          {bottomOverlay}
        </div>
      ) : null}
    </section>
  );
}

CourseSectionShell.displayName = 'CourseSectionShell';
